import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { useToastStack } from '@/components/feedback/toast-stack'
import { DateInput } from '@/components/filters/DateInput'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCashFirm, useInvalidateCash } from '@/features/cash/hooks'
import { CashFirmSelect } from '@/features/cash/shared'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import { formatCubeDateTime, formatDateOnly, formatDateTime, formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import {
  cashApi,
  type CashDecision,
  type CashHealthItem,
  type CashRealizedItem,
} from '@/services/http/cash'

const t = messages.cash.health
const money = (v: number) => formatDecimal(v, 2)

function useAction<T>(fn: (body: T) => Promise<unknown>, success: string) {
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      toast({ type: 'success', title: success })
      void invalidate()
    },
    onError: (error) =>
      toast({
        type: 'error',
        title: messages.cash.planning.failed,
        description: toProblem(error).detail ?? undefined,
      }),
  })
}

function Section({
  id,
  title,
  hint,
  summary,
  children,
}: {
  id?: string
  title: string
  hint?: string
  summary?: string
  children?: React.ReactNode
}) {
  return (
    <section
      id={id}
      className="bg-surface flex scroll-mt-4 flex-col gap-2 rounded-lg border p-4"
      aria-label={title}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        {summary && (
          <span className="text-sm tabular-nums" data-testid="cash-health-summary">
            {summary}
          </span>
        )}
      </div>
      {hint && <p className="text-text-secondary text-xs">{hint}</p>}
      {children}
    </section>
  )
}

function DecisionRow({
  firm,
  kind,
  item,
  canPlan,
}: {
  firm: string
  kind: CashDecision['kind']
  item: CashHealthItem
  canPlan: boolean
}) {
  const [reason, setReason] = useState(item.note ?? '')
  const mutation = useAction(cashApi.decision, t.saved)
  const decide = (excluded: boolean | null) =>
    mutation.mutate({ firm, kind, key: item.key, excluded, note: reason || null })
  const label = item.label || item.key
  return (
    <tr className="border-b align-top last:border-0" data-key={item.key}>
      <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{formatDateOnly(item.date)}</td>
      <td className="py-2 pr-3">
        {label}
        {item.detail && <span className="text-text-secondary block text-xs">{item.detail}</span>}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">{money(item.amount)}</td>
      <td className="py-2 pr-3">
        <Badge size="sm" tone={item.excluded ? 'warning' : 'success'}>
          {item.excluded ? t.excluded : t.included}
        </Badge>
        {item.excluded === item.default_excluded && (
          <span className="text-text-secondary ml-1 text-xs">({t.byDefault})</span>
        )}
        {item.decided_by_name && item.decided_at && (
          <span className="text-text-secondary block text-xs">
            {t.decided(item.decided_by_name, formatDateTime(item.decided_at))}
          </span>
        )}
      </td>
      <td className="py-2">
        {canPlan && (
          <div className="flex flex-wrap items-center gap-1">
            <Input
              className="h-8 w-40"
              placeholder={t.reason}
              aria-label={`${t.reason} ${label}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button
              size="sm"
              variant={item.excluded ? 'default' : 'secondary'}
              aria-label={`${item.excluded ? t.include : t.exclude} ${label}`}
              loading={mutation.isPending}
              onClick={() => decide(!item.excluded)}
            >
              {item.excluded ? t.include : t.exclude}
            </Button>
            {item.excluded !== item.default_excluded && (
              <Button size="sm" variant="ghost" onClick={() => decide(null)}>
                {t.reset}
              </Button>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

function DecisionTable({
  firm,
  kind,
  items,
  canPlan,
}: {
  firm: string
  kind: CashDecision['kind']
  items: CashHealthItem[]
  canPlan: boolean
}) {
  if (!items.length) return <p className="text-text-secondary text-sm">{t.none}</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-text-secondary border-b text-left text-xs">
            <th className="py-2 pr-3 font-medium">{t.columns.date}</th>
            <th className="py-2 pr-3 font-medium">{t.columns.label}</th>
            <th className="py-2 pr-3 text-right font-medium">{t.columns.amount}</th>
            <th className="py-2 pr-3 font-medium">{t.columns.state}</th>
            <th className="py-2 font-medium">{t.columns.decision}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <DecisionRow key={item.key} firm={firm} kind={kind} item={item} canPlan={canPlan} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function RealizedRow({
  firm,
  item,
  canPlan,
}: {
  firm: string
  item: CashRealizedItem
  canPlan: boolean
}) {
  const [date, setDate] = useState('')
  const mutation = useAction(
    (v: { done: boolean; date: string | null }) => cashApi.realize(firm, item.id, v.done, v.date),
    t.saved,
  )
  return (
    <tr className="border-b last:border-0" data-id={item.id}>
      <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
        {formatDateOnly(item.payment_date)}
      </td>
      <td className="py-2 pr-3">
        {item.type}
        {item.description && (
          <span className="text-text-secondary block text-xs">{item.description}</span>
        )}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">{money(item.amount)}</td>
      <td className="py-2">
        {canPlan && (
          <div className="flex flex-wrap items-center gap-1">
            <Button
              size="sm"
              loading={mutation.isPending}
              onClick={() => mutation.mutate({ done: true, date: null })}
            >
              {t.realizedDone}
            </Button>
            <DateInput value={date} onChange={setDate} aria-label={t.newDate} />
            <Button
              size="sm"
              variant="secondary"
              disabled={!date}
              onClick={() => mutation.mutate({ done: false, date })}
            >
              {t.postpone}
            </Button>
          </div>
        )}
      </td>
    </tr>
  )
}

/** /nakit/veri-sagligi — nakde girmeyen kayıtlar ve kararlar (docs/NAKIT-PLANI-PLAN.md Faz 3). */
export function HealthPage() {
  const { firms, firm, setFirm, can } = useCashFirm()
  const canPlan = can('cash.plan')
  const health = useQuery({
    queryKey: queryKeys.cash.health(firm ?? ''),
    queryFn: () => cashApi.health(firm!),
    enabled: !!firm,
  })
  const realized = useQuery({
    queryKey: queryKeys.cash.realized(firm ?? ''),
    queryFn: () => cashApi.realizedQueue(firm!),
    enabled: !!firm,
  })
  const h = health.data
  const { hash } = useLocation()
  // Yapılacaklar bağlantısı (`#pos`, `#gerceklesti`): bölüm veri gelince çizildiği için kaydırma sonra.
  useEffect(() => {
    if (h && hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' })
  }, [h, hash])
  const overduePos = (h?.pos ?? []).filter((p) => p.default_excluded)

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t.title}
        description={t.description}
        actions={<CashFirmSelect firms={firms.data ?? []} firm={firm} onChange={setFirm} />}
      />
      {health.isError ? (
        <ErrorState error={health.error} onRetry={() => void health.refetch()} />
      ) : !h || !firm ? (
        <Loading rows={8} />
      ) : (
        <>
          <p className="text-text-secondary flex flex-wrap gap-x-4 text-xs">
            <span>{messages.cash.weekly.start(formatDateOnly(h.start))}</span>
            {h.cari_snapshot_at && (
              <span>
                {messages.cash.weekly.cariSnapshot(formatCubeDateTime(h.cari_snapshot_at))}
              </span>
            )}
            {h.bank_snapshot_at && (
              <span>
                {messages.cash.weekly.bankSnapshot(formatCubeDateTime(h.bank_snapshot_at))}
              </span>
            )}
          </p>
          <Section
            id="pos"
            title={t.pos}
            hint={t.posHint}
            summary={t.overdue(
              overduePos.length,
              money(overduePos.reduce((s, p) => s + p.amount, 0)),
            )}
          >
            <DecisionTable firm={firm} kind="pos" items={h.pos} canPlan={canPlan} />
          </Section>
          <Section title={t.cards} hint={t.cardsHint}>
            <DecisionTable firm={firm} kind="kart" items={h.card_periods} canPlan={canPlan} />
          </Section>
          <Section id="gerceklesti" title={t.realized} hint={t.realizedHint}>
            {!realized.data?.length ? (
              <p className="text-text-secondary text-sm">{t.realizedEmpty}</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {realized.data.map((item) => (
                    <RealizedRow key={item.id} firm={firm} item={item} canPlan={canPlan} />
                  ))}
                </tbody>
              </table>
            )}
          </Section>
          <Section
            title={t.control}
            hint={t.controlHint}
            summary={t.count(h.control.count, money(h.control.amount))}
          />
          <Section
            title={t.erpOutside}
            summary={t.count(h.erp_outside.count, money(h.erp_outside.amount))}
          />
          <Section title={t.unknownCodes} hint={t.unknownHint}>
            {h.unknown_codes.length === 0 ? (
              <p className="text-text-secondary text-sm">{t.none}</p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {h.unknown_codes.map((c) => (
                  <li key={c.code}>
                    <Badge tone="warning">
                      {c.code}: {t.count(c.count, money(c.amount))}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      )}
    </div>
  )
}
